import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { BriefingPreviewView } from '@src/app/features/dashboard/commercial/pages/deal-detail/components/deal-briefing-tab/briefing-preview.store';
import { SharedModule } from '@src/app/shared/shared.module';
import {
  PREVIEW_ZOOM_MAX,
  PREVIEW_ZOOM_MIN,
  PreviewZoom,
  nextPreviewZoom,
} from '@src/app/features/dashboard/commercial/pages/deal-detail/components/deal-briefing-tab/briefing-presenter';

/** Mensagem que o documento da visualização manda ao terminar de paginar (contrato do Q3). */
interface PreviewMessage {
  source?: unknown;
  type?: unknown;
  pageCount?: unknown;
  height?: unknown;
  /** `scaled` (Q11): escala aplicada e a de "ajustar à largura". */
  scale?: unknown;
  fitScale?: unknown;
}

/** Mensagem que a tela manda ao documento para trocar o zoom (contrato do `briefing-preview.js` do Doc-Generate). */
const PREVIEW_HOST_SOURCE = 'cognito-briefing-host';

/** Um documento carregado num iframe. `id` é o `track` do `@for`: o mesmo id mantém o mesmo elemento (sem recarga). */
interface FrameSlot {
  id: number;
  html: SafeHtml;
  /** Altura do documento paginado, quando ele avisa; o iframe passa a ter essa altura e quem rola é o contêiner. */
  height: number | null;
  pageCount: number | null;
  /** Escala das folhas que o documento avisou (`scaled`); nula em documento de antes do zoom. */
  scale: number | null;
}

const PREVIEW_MESSAGE_SOURCE = 'cognito-briefing-preview';

/** Erro da visualização numa versão pronta: o PDF continua disponível (G2 da crítica de design). */
export const READY_PREVIEW_ERROR =
  'Não foi possível mostrar a visualização agora. O PDF da versão continua disponível nos ícones Baixar PDF e Abrir PDF da barra da versão.';

/** Tempo-limite da troca: se o Paged.js não avisar, o rascunho novo entra assim mesmo (o pior caso é o de antes). */
export const PREVIEW_SWAP_TIMEOUT_MS = 4000;

/** Teto da altura aceita do documento (folhas A4 em escala 1, com folga), contra valor absurdo na mensagem. */
const MAX_DOCUMENT_HEIGHT = 20000;

/**
 * Visualização em HTML de uma versão do Briefing (card 3878, Q3), dentro de `iframe` com `srcdoc` e
 * `sandbox="allow-scripts"` SEM `allow-same-origin`, exatamente como o contrato do backend manda: o documento roda
 * com origem opaca, sem cookie, storage nem acesso à plataforma (a CSP vem na meta do próprio HTML).
 *
 * **Troca sem piscar (Menor 4 da revisão).** Rascunho novo da mesma versão carrega num segundo iframe, escondido
 * (`visibility: hidden`, que ainda faz layout e deixa o Paged.js paginar), e só toma o lugar do atual quando o
 * documento avisa que paginou (`postMessage` `rendered` ou `failed`) ou no tempo-limite de 4 s. Os dois iframes vêm de
 * um `@for` com `track` pelo id: ao promover, o elemento do novo é o mesmo, então ele não recarrega.
 *
 * **Rolagem.** Dentro do iframe sem same-origin não dá para ler a rolagem; por isso, quando o documento avisa a
 * altura, o iframe ganha a altura total dele e quem rola é o contêiner de fora, que a tela lê. Na troca, a posição
 * proporcional é mantida se o documento tem mais de uma página e a pessoa rolou. Troca de versão volta ao topo.
 */
@Component({
  selector: 'app-briefing-preview-frame',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './briefing-preview-frame.component.html',
  styleUrl: './briefing-preview-frame.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BriefingPreviewFrameComponent {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly view = input.required<BriefingPreviewView>();
  /** Versão mostrada: trocar de versão não faz a troca suave (volta ao topo, sem esperar o novo paginar). */
  readonly versionId = input<string | null>(null);
  /** A versão está na fila ou gerando (muda o texto do esqueleto). */
  readonly generating = input(false);
  readonly versionNumber = input<number | null>(null);

  /**
   * Texto do erro da visualização (G2 da crítica): numa versão pronta, o PDF continua à mão e a tela diz isso; o texto
   * do backend, que fala do próximo rascunho, só vale enquanto a versão está gerando.
   */
  protected readonly errorMessage = computed(() =>
    this.generating()
      ? this.view().message ||
        'A visualização não abriu agora. Ela volta sozinha no próximo rascunho; use Tentar de novo para forçar.'
      : READY_PREVIEW_ERROR
  );

  readonly retry = output<void>();
  /**
   * Páginas montadas na visualização mostrada (nulo até o documento avisar). O aviso "Prévia na tela; o documento
   * oficial é o PDF · N páginas" mora na barra do pé do leitor, na aba (Q11).
   */
  readonly pageCountChange = output<number | null>();

  /** `[atual]` ou `[atual, novo carregando escondido]`. */
  protected readonly slots = signal<FrameSlot[]>([]);
  protected readonly activeSlot = computed(() => this.slots()[0] ?? null);
  /** Páginas que o Paged.js montou na visualização mostrada; nulo até o documento avisar. */
  protected readonly pageCount = computed(() => this.activeSlot()?.pageCount ?? null);

  /**
   * Zoom pedido (vale para a versão aberta e para os rascunhos seguintes); "ajustar à largura" por padrão. Público: o
   * controle mora na barra do pé do leitor, na aba (por referência de template), para não cobrir o documento.
   */
  readonly zoom = signal<PreviewZoom>('fit');
  /**
   * Escala que o documento mostrou por último; nula enquanto ele não avisa (ou com erro na visualização). Documento
   * de antes do zoom (Doc-Generate antigo) nunca avisa, e o controle não aparece.
   */
  readonly shownScale = computed(() =>
    this.view().status === 'error' ? null : (this.activeSlot()?.scale ?? null)
  );
  /** − e + desligam nos limites do controle (50% e 200%). */
  readonly canZoomOut = computed(() => (this.shownScale() ?? 0) > PREVIEW_ZOOM_MIN + 0.001);
  readonly canZoomIn = computed(() => (this.shownScale() ?? Infinity) < PREVIEW_ZOOM_MAX - 0.001);
  readonly zoomPercent = computed(() => {
    const scale = this.shownScale();
    return scale === null ? '' : `${Math.round(scale * 100)}%`;
  });

  private readonly scroller = viewChild<ElementRef<HTMLElement>>('scroller');
  private nextSlotId = 1;
  private shownVersionId: string | null = null;
  private swapTimer: ReturnType<typeof setTimeout> | null = null;
  private scrollTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    effect(() => {
      const pages = this.pageCount();
      untracked(() => this.pageCountChange.emit(pages));
    });

    effect(() => {
      const html = this.view().html;
      const versionId = this.versionId();
      untracked(() => this.receive(html, versionId));
    });

    inject(DestroyRef).onDestroy(() => {
      this.clearSwapTimer();
      if (this.scrollTimer) clearTimeout(this.scrollTimer);
    });
  }

  /**
   * Aceita só a mensagem de um dos próprios iframes (`event.source`) com o `source` do contrato; a origem chega como
   * "null" (sandbox sem same-origin), por isso não dá para filtrar por `origin`. Nada do conteúdo vem na mensagem.
   */
  @HostListener('window:message', ['$event'])
  protected onMessage(event: MessageEvent): void {
    const slotId = this.slotIdOf(event.source);
    if (slotId === null) return;

    const data = event.data as PreviewMessage | null;
    if (data?.source !== PREVIEW_MESSAGE_SOURCE) return;

    if (data.type === 'rendered') {
      this.slots.update(slots =>
        slots.map(slot =>
          slot.id === slotId
            ? {
                ...slot,
                height: validNumber(data.height, MAX_DOCUMENT_HEIGHT),
                pageCount: validNumber(data.pageCount, 999),
              }
            : slot
        )
      );
      // Zoom escolhido antes vale também para o documento novo (rascunho seguinte, troca de versão).
      const zoom = this.zoom();
      if (zoom !== 'fit') this.sendZoom(zoom, event.source);
    }
    // Escala nova (resize, zoom): a altura do documento muda junto e o iframe acompanha.
    if (data.type === 'scaled') {
      const scale = validScale(data.scale);
      const height = validNumber(data.height, MAX_DOCUMENT_HEIGHT);
      this.slots.update(slots =>
        slots.map(slot =>
          slot.id === slotId ? { ...slot, scale, height: height ?? slot.height } : slot
        )
      );
    }
    if ((data.type === 'rendered' || data.type === 'failed') && this.slots()[1]?.id === slotId) {
      this.promote();
    }
  }

  /** HTML novo da store: primeira carga e troca de versão entram direto; rascunho novo da mesma versão espera. */
  private receive(html: SafeHtml | null, versionId: string | null): void {
    const slots = this.slots();
    const versionChanged = versionId !== this.shownVersionId;
    this.shownVersionId = versionId;

    if (!html) {
      this.clearSwapTimer();
      this.slots.set([]);
      return;
    }
    if (versionChanged || slots.length === 0) {
      this.clearSwapTimer();
      this.slots.set([this.newSlot(html)]);
      this.scrollTo(0);
      return;
    }
    if (slots[0].html === html || slots[1]?.html === html) return;

    // Rascunho mais novo enquanto outro ainda carrega: o novo toma o lugar do que carregava, e o prazo recomeça.
    this.slots.set([slots[0], this.newSlot(html)]);
    this.clearSwapTimer();
    this.swapTimer = setTimeout(() => this.promote(), PREVIEW_SWAP_TIMEOUT_MS);
  }

  /** O documento novo toma o lugar do atual, mantendo a rolagem proporcional quando faz sentido. */
  private promote(): void {
    const [current, incoming] = this.slots();
    if (!incoming) return;
    this.clearSwapTimer();

    const ratio = this.scrollRatio(current);
    this.slots.set([incoming]);
    this.scrollTo(ratio !== null && incoming.height ? ratio : 0);
  }

  /** Posição relativa da rolagem (0 a 1), só com mais de uma página e a pessoa tendo rolado. */
  private scrollRatio(slot: FrameSlot): number | null {
    const element = this.scroller()?.nativeElement;
    if (!element || (slot.pageCount ?? 0) <= 1 || element.scrollTop <= 0) return null;
    const range = element.scrollHeight - element.clientHeight;
    return range > 0 ? Math.min(1, element.scrollTop / range) : null;
  }

  /** Aplica a rolagem depois de o Angular desenhar o iframe novo na altura nova. */
  private scrollTo(ratio: number): void {
    if (this.scrollTimer) clearTimeout(this.scrollTimer);
    this.scrollTimer = setTimeout(() => {
      this.scrollTimer = null;
      const element = this.scroller()?.nativeElement;
      if (!element) return;
      element.scrollTop = Math.round(
        ratio * Math.max(0, element.scrollHeight - element.clientHeight)
      );
    });
  }

  private newSlot(html: SafeHtml): FrameSlot {
    return { id: this.nextSlotId++, html, height: null, pageCount: null, scale: null };
  }

  /** − e + do controle: 10% a partir da escala mostrada. */
  stepZoom(direction: 1 | -1): void {
    const current = this.shownScale();
    if (current === null) return;
    this.applyZoom(nextPreviewZoom(current, direction));
  }

  /** "Ajustar à largura": a folha preenche a coluna (até 1,4x). */
  fitWidth(): void {
    this.applyZoom('fit');
  }

  private applyZoom(zoom: PreviewZoom): void {
    this.zoom.set(zoom);
    for (const frame of this.frames()) this.sendZoom(zoom, frame.contentWindow);
  }

  /**
   * Pede o zoom ao documento. Origem "*": o documento tem origem opaca (sandbox sem same-origin) e a mensagem só leva a
   * escala; quem aceita confere que veio do próprio pai.
   */
  private sendZoom(zoom: PreviewZoom, target: MessageEventSource | Window | null): void {
    if (!target || !('postMessage' in target)) return;
    (target as Window).postMessage({ source: PREVIEW_HOST_SOURCE, type: 'zoom', scale: zoom }, '*');
  }

  private frames(): HTMLIFrameElement[] {
    return Array.from(
      this.host.nativeElement.querySelectorAll<HTMLIFrameElement>('iframe[data-slot]')
    );
  }

  /** De qual dos nossos iframes veio a mensagem (`contentWindow` igual ao `event.source`). */
  private slotIdOf(source: MessageEventSource | null): number | null {
    if (!source) return null;
    for (const frame of this.frames()) {
      if (frame.contentWindow === source) return Number(frame.dataset['slot']);
    }
    return null;
  }

  private clearSwapTimer(): void {
    if (this.swapTimer) clearTimeout(this.swapTimer);
    this.swapTimer = null;
  }
}

/** Escala que o documento avisou, entre 0,1x e 3x; fora disso, nula. */
function validScale(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0.1 && value <= 3
    ? value
    : null;
}

function validNumber(value: unknown, max: number): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.round(Math.min(value, max))
    : null;
}
